import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { APIResponse } from '../../../services/interface'
import { ApiGet } from '../../../services/Service'
import { StatusEnum } from '../../type'

export interface IDoctorPerformance {
  chiropractorId: string
  firstname: string
  lastname: string
  clinicName: string
  totalAdjustments: number
  newPatientsSeen: number
  membersConverted: number
  conversionRatio: number
  conversionBenchmark: number
  membersRetained: number
  retentionRatio: number
  retentionBenchmark: number
  retargetRatio: number
  retargetBenchmark: number
  retargetConvertRatio: number
  retargetConvertBenchmark: number
  personalRating: number
  ratingBenchmark: number
}

export interface ITimeseriesDataPoint {
  date: string
  value: number
}

export interface ITimeseriesSeries {
  id: string
  name: string
  clinicName: string
  dataPoints: ITimeseriesDataPoint[]
}

export interface ITimeseriesResponse {
  metric: string
  series: ITimeseriesSeries[]
}

export interface AdminPerformanceState {
  performances: Array<IDoctorPerformance>
  performancesStatus: StatusEnum
  timeseries: ITimeseriesResponse | null
  timeseriesStatus: StatusEnum
  error: any
}

const initialState: AdminPerformanceState = {
  performances: [],
  performancesStatus: StatusEnum.Idle,
  timeseries: null,
  timeseriesStatus: StatusEnum.Idle,
  error: null,
}

export const getPerformancesAsync = createAsyncThunk(
  'performance/getPerformances',
  async ({ period }: { period: 'all' | '30d' }, thunkAPI) => {
    const response: APIResponse = await ApiGet(`/operator/chiropractor/performance?period=${period}`)
      .then((res) => res)
      .catch((err) => err)
    if (!response.status) {
      return thunkAPI.rejectWithValue('Network error. Please check your internet.')
    } else if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue({
        data: response.data.data,
      })
    } else {
      return thunkAPI.rejectWithValue('Network error. Please check your internet.')
    }
  },
)

export const getPerformanceTimeseriesAsync = createAsyncThunk(
  'performance/getTimeseries',
  async ({ metric, days }: { metric: string; days: number }, thunkAPI) => {
    const response: APIResponse = await ApiGet(
      `/operator/chiropractor/performance/timeseries?metric=${metric}&days=${days}`,
    )
      .then((res) => res)
      .catch((err) => err)
    if (!response.status) {
      return thunkAPI.rejectWithValue('Network error. Please check your internet.')
    } else if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue({ data: response.data.data })
    } else {
      return thunkAPI.rejectWithValue('Network error. Please check your internet.')
    }
  },
)

export const performanceSlice = createSlice({
  name: 'performance',
  initialState,
  reducers: {
    resetPerformancesStatus: (state) => {
      state.performancesStatus = StatusEnum.Idle
    },
    resetTimeseriesStatus: (state) => {
      state.timeseriesStatus = StatusEnum.Idle
    },
  },
  extraReducers(builder) {
    builder.addCase(getPerformancesAsync.pending, (state) => {
      state.performancesStatus = StatusEnum.Pending
    })
    builder.addCase(getPerformancesAsync.fulfilled, (state, action) => {
      state.performances = action.payload.data
      state.performancesStatus = StatusEnum.Success
    })
    builder.addCase(getPerformancesAsync.rejected, (state, action) => {
      state.performancesStatus = StatusEnum.Failed
      state.error = action?.payload
    })
    builder.addCase(getPerformanceTimeseriesAsync.pending, (state) => {
      state.timeseriesStatus = StatusEnum.Pending
    })
    builder.addCase(getPerformanceTimeseriesAsync.fulfilled, (state, action) => {
      state.timeseries = action.payload.data
      state.timeseriesStatus = StatusEnum.Success
    })
    builder.addCase(getPerformanceTimeseriesAsync.rejected, (state, action) => {
      state.timeseriesStatus = StatusEnum.Failed
      state.error = action?.payload
    })
  },
})

export default performanceSlice.reducer
export const { resetPerformancesStatus, resetTimeseriesStatus } = performanceSlice.actions
